"use client";

import clsx from "clsx";
import { JSONSchema7 } from "json-schema";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { defaultCountries } from "react-international-phone";
import { FormToolType } from "@/enums/ai";
import { SupportedCountries } from "@/enums/internationalization";
import { URL_REG_EXP } from "@/utils/common/general";
import { emailRegex } from "@/utils/common/regex";
import PhoneInput from "../common/PhoneInput";
import Button from "../library/Button";
import Icon, { ICON_SIZES } from "../library/Icon";
import Input from "../library/Input";
import Select from "../library/Select";
import TextArea from "../library/TextArea";

type AskFormProps = {
  callId: string;
  schemaText: string;
  countries: SupportedCountries[];
  submitForm: (
    callId: string,
    data: Record<string, string | number>
  ) => Promise<boolean>;
};

const AskForm: React.FC<AskFormProps> = ({
  callId,
  schemaText,
  countries,
  submitForm,
}) => {
  const { t } = useTranslation("ask");
  const { t: ct } = useTranslation("common");

  const [formData, setFormData] = React.useState<JSONSchema7 | undefined>();
  const [formValidations, setFormValidations] = React.useState<
    Record<string, any>
  >({});
  const [phoneValidations, setPhoneValidations] = React.useState<
    Record<string, string>
  >({});
  const [formValues, setFormValues] = React.useState<
    Record<string, string | number>
  >({});
  const [formSubmitted, setFormSubmitted] = React.useState(false);

  useEffect(() => {
    const parsedSchema: JSONSchema7 = JSON.parse(schemaText);
    setFormData(parsedSchema);
  }, [schemaText]);

  useEffect(() => {
    if (!formData) {
      return;
    }

    interface ValidationAcc {
      [key: string]: string;
    }

    setFormValidations(
      Object.keys(formData.properties || {}).reduce(
        (acc: ValidationAcc, key: string) => {
          const value = (formData.properties?.[key] as any)?.default;
          const item = (JSON.parse(schemaText) as JSONSchema7).properties?.[
            key
          ];
          const validation = (item as any)?.validation;

          if (validation?.required && !value) {
            return { ...acc, [key]: t("helpTextRequired") };
          }

          if ((item as any)?.type === FormToolType.Phone) {
            //return the value already set in the validations
            return {
              ...acc,
              [key]: phoneValidations[key],
            };
          }

          if (
            validation?.minLength &&
            typeof value === "string" &&
            value?.length < validation.minLength
          ) {
            return {
              ...acc,
              [key]: t("helpTextMinLength", { length: validation.minLength }),
            };
          }

          if (
            validation?.maxLength &&
            typeof value === "string" &&
            value?.length > validation.maxLength
          ) {
            return {
              ...acc,
              [key]: t("helpTextMaxLength", { length: validation.maxLength }),
            };
          }

          if (
            validation?.minValue &&
            typeof value === "number" &&
            value < validation.minValue
          ) {
            return {
              ...acc,
              [key]: t("helpTextMinValue", { value: validation.minValue }),
            };
          }

          if (
            validation?.maxValue &&
            typeof value === "number" &&
            value > validation.maxValue
          ) {
            return {
              ...acc,
              [key]: t("helpTextMaxValue", { value: validation.maxValue }),
            };
          }

          if (
            validation?.pattern &&
            typeof value === "string" &&
            !new RegExp(validation.pattern).test(value)
          ) {
            return { ...acc, [key]: t("helpTextPattern") };
          }

          if (
            (item as any)?.type === FormToolType.Email &&
            typeof value === "string" &&
            !emailRegex.test(value) &&
            (value?.length > 0 || validation?.required)
          ) {
            return { ...acc, [key]: t("helpTextEmail") };
          }

          if (
            (item as any)?.type === FormToolType.Url &&
            typeof value === "string" &&
            !URL_REG_EXP.test(value) &&
            (value?.length > 0 || validation?.required)
          ) {
            return { ...acc, [key]: t("helpTextUrl") };
          }

          return { ...acc, [key]: "" };
        },
        {} as ValidationAcc
      )
    );

    setFormValues(
      Object.keys(formData.properties || {}).reduce((acc, key) => {
        const value = (formData.properties?.[key] as any)?.default;
        return { ...acc, [key]: value };
      }, {} as Record<string, string | number>)
    );
  }, [formData]);

  const getComponent = (
    item: JSONSchema7,
    key: string,
    isRequired: boolean
  ) => {
    if (item.type !== "string") {
      return null;
    }

    switch (item.format) {
      case FormToolType.TextArea:
        return (
          <TextArea
            value={(formValues[key] || "") as string}
            onChange={(val) =>
              setFormValues((prev) => ({ ...prev, [key]: val }))
            }
            containerClassName="!mt-0 !mb-[-0.5rem]"
            label={item.title}
            helperText={formValidations[key]}
            disabled={formSubmitted}
          />
        );
      case FormToolType.Phone:
        return (
          <PhoneInput
            label={item.title ?? ct("phone")}
            value={(formValues[key] || "") as string}
            initialCountry={countries.length > 0 ? countries[0] : undefined}
            allowedCountries={countries}
            setValue={(val) => {
              setFormValues((prev) => ({ ...prev, [key]: val }));
            }}
            helperText={phoneValidations[key]}
            required={isRequired}
            setValid={(isValid) => {
              if (
                !isValid &&
                !isRequired &&
                ((((formValues[key] || "") as string).length || 0) === 0 ||
                  defaultCountries.findIndex(
                    (c) =>
                      (c[2] as string) ===
                      ((formValues[key] || "") as string).replace("+", "")
                  ) >= 0)
              ) {
                setPhoneValidations((prev) => ({
                  ...prev,
                  [key]: "",
                }));
              } else {
                setPhoneValidations((prev) => ({
                  ...prev,
                  [key]: isValid ? "" : t("helpTextPhone"),
                }));
              }
            }}
            disabled={formSubmitted}
          />
        );
      default:
        if (item.oneOf) {
          return (
            <Select
              label={item.title}
              value={(formValues[key] || "") as string}
              options={(item.oneOf || []).map((option) => {
                const typedOption = option as { const: string; title: string };
                return {
                  label: typedOption.title,
                  value: typedOption.const,
                };
              })}
              onChange={(e: { target: { value: string } }) => {
                setFormValues((prev) => ({
                  ...prev,
                  [key]: e.target.value,
                }));
              }}
              helperText={formValidations[key]}
              required={isRequired}
              disabled={formSubmitted}
            />
          );
        }
        return (
          <Input
            type={
              item.format === FormToolType.Number
                ? "number"
                : item.format === FormToolType.Email
                ? "email"
                : item.format === FormToolType.Url
                ? "url"
                : "text"
            }
            icon={
              item.format === FormToolType.Id ? (
                <Icon icon="lock" size={ICON_SIZES.LG} className="w-6" />
              ) : undefined
            }
            label={item.title}
            value={formValues[key] || ""}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                [key]:
                  item.type === FormToolType.Number
                    ? Number(e.target.value)
                    : e.target.value,
              }))
            }
            helperText={formValidations[key]}
            required={isRequired}
            disabled={formSubmitted}
          />
        );
    }
  };

  if (
    !formData?.properties ||
    typeof formData.properties !== "object" ||
    Object.keys(formData.properties).length === 0
  ) {
    return <div>{t("errorCreatingForm")}</div>;
  }

  return (
    <div
      className={clsx("grid", "gap-4", "lg:grid-cols-2", "[&_p]:text-red-500")}
    >
      {Object.keys(formData.properties).map((key) => {
        const r: JSONSchema7 = formData.properties?.[key] as JSONSchema7;
        return (
          <div
            key={key}
            className={clsx({
              "lg:col-span-2":
                (r as JSONSchema7)?.format === FormToolType.TextArea,
            })}
          >
            {getComponent(r, key, (formData.required || []).includes(key))}
          </div>
        );
      })}
      <div>
        <Button
          wide
          className={clsx("!mt-5", {
            "cursor-default hover:bg-button_primary": formSubmitted,
          })}
          onClick={async () => {
            if (formSubmitted) {
              return;
            }

            const isSuccess = await submitForm(callId, formValues);

            if (isSuccess) {
              setFormSubmitted(true);
            }
          }}
          disabled={
            Object.values(formValidations).some((item) => !!item) ||
            Object.values(phoneValidations).some((item) => !!item) ||
            (formData.required &&
              formData.required.some(
                (key) => formValues[key] === undefined || formValues[key] === ""
              ))
          }
        >
          {formSubmitted ? ct("submitted") : ct("submit")}
        </Button>
      </div>
    </div>
  );
};

export default AskForm;
